import { useMemo, useState } from "react";
import { OrderedMap } from "immutable";
import { useQuery, useQueryClient, useMutation, keepPreviousData } from "@tanstack/react-query";
import i18n from "i18n";
import UserNotification from "util/UserNotification";
import useServerText from "util/serverText";
import { SLICING_QUERY_KEY } from "components/common/PaginatedEntityTable/slicing/queryKeys";
import { defaultOnError } from "util/conditional/onError";
import usePermissions from "hooks/usePermissions";
import useLicenseCheck from "license/useLicenseCheck";
import { assetsQueryKey, assetQueryKey } from "./queryKeys";
import {
  searchAssets,
  searchAssetsKeyFC,
  fetchAssetSlices,
  fetchAsset,
  deleteAsset,
  createAsset,
  updateAsset,
  fetchPriorities,
  fetchCategories,
  addPriority,
  updatePriority,
  reprioritizePriorities,
  deletePriority,
  addCategory,
  updateCategory,
  deleteCategory,
  bulkAddCategories,
  fetchAssetSources,
  createAssetSource,
  updateAssetSource,
  deleteAssetSource,
  testAssetSource,
  fetchAssetSourceMappings,
  createAssetSourceMapping,
  updateAssetSourceMapping,
  deleteAssetSourceMapping,
  importAssets,
  bulkTestAssetSource,
  testNewAssetSourceMapping,
  testSavedAssetSourceMapping,
  fetchAssetSource,
  fetchMS365DeploymentTypes,
  fetchAssetSourceMapping,
  toggleMappingSync,
  fetchScanners,
  addScanner,
  deleteScanner,
  editScanner,
  toggleScannerSync,
  testNewScannerConnection,
  testScannerConnection,
  importVulnerabilityScans,
  fetchAssetLabels,
  getAssetById
} from "./api/assetsAPI";
export const useIsAllowedToFetchAssets = () => {
  const { isPermitted } = usePermissions();
  const { security } = useLicenseCheck();
  return isPermitted(["asset:read"]) && security.isValid;
};
export function useGetAssetLabels({ page, perPage, query = void 0 }) {
  const isAllowedToFetchAssets = useIsAllowedToFetchAssets();
  const { data, isInitialLoading } = useQuery({
    queryKey: assetsQueryKey("labels", page, perPage, query),
    queryFn: () => defaultOnError(
      fetchAssetLabels(page, perPage, query),
      i18n.t("notifications.fetch_asset_labels_failed", { ns: "enterprise_security_assets" })
    ),
    retry: 2,
    placeholderData: keepPreviousData,
    enabled: isAllowedToFetchAssets
  });
  return {
    initialLoadingAssets: isInitialLoading,
    assetLabels: isInitialLoading ? [] : data == null ? void 0 : data.assets,
    pagination: {
      page: (data == null ? void 0 : data.page) || page,
      perPage: (data == null ? void 0 : data.per_page) || perPage,
      total: (data == null ? void 0 : data.total) || 0,
      grandTotal: (data == null ? void 0 : data.grand_total) || 0,
      count: (data == null ? void 0 : data.count) || 0
    }
  };
}
export const useSearchAssets = () => ({ searchAssets, searchAssetsKeyFC, fetchAssetSlices });
export function useGetAssetsCount(filters = OrderedMap()) {
  var _a, _b;
  const searchParams = {
    page: 1,
    pageSize: 1,
    query: "",
    sort: { attributeId: "risk_score_normalized", direction: "desc" },
    filters
  };
  const { data, isLoading } = useQuery({
    queryKey: searchAssetsKeyFC(searchParams),
    queryFn: () => defaultOnError(
      searchAssets(searchParams),
      i18n.t("notifications.fetch_assets_failed", { ns: "enterprise_security_assets" })
    ),
    retry: 2
  });
  return {
    loadingCount: isLoading,
    count: (_b = (_a = data == null ? void 0 : data.pagination) == null ? void 0 : _a.total) != null ? _b : 0
  };
}
const assetQueryOptions = (assetId) => ({
  queryKey: assetQueryKey(assetId),
  queryFn: () => defaultOnError(
    fetchAsset(assetId),
    i18n.t("notifications.fetch_asset_failed", { ns: "enterprise_security_assets" })
  ),
  retry: 1
});
export function useGetAsset(assetId) {
  const { data, isFetching } = useQuery({
    ...assetQueryOptions(assetId),
    enabled: !!assetId
  });
  return {
    asset: isFetching ? null : data,
    loadingAsset: isFetching
  };
}
export function useFetchAsset() {
  const queryClient = useQueryClient();
  return (assetId) => queryClient.fetchQuery(assetQueryOptions(assetId)).catch(() => void 0);
}
export function useGetAssetsByIds(assetIds) {
  const { data, isFetching } = useQuery({
    queryKey: ["get-asset-by-ids", assetIds],
    queryFn: () => Promise.all(
      assetIds.map(
        (assetId) => defaultOnError(
          getAssetById(assetId),
          i18n.t("notifications.fetch_assets_failed", { ns: "enterprise_security_assets" })
        )
      )
    ),
    retry: 1,
    enabled: assetIds.length > 0,
    placeholderData: []
  });
  return {
    assets: data.filter((asset) => !!asset),
    fetchingAssets: isFetching
  };
}
const invalidateAssetsTableQueries = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: assetsQueryKey() });
  queryClient.invalidateQueries({ queryKey: [SLICING_QUERY_KEY] });
};
export function useCreateAsset() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: createAsset,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.asset_created", { ns: "enterprise_security_assets" }));
      invalidateAssetsTableQueries(queryClient);
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    createAsset: mutateAsync,
    creatingAsset: isLoading
  };
}
export function useUpdateAsset() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateAsset,
    onSuccess: (_, { assetId }) => {
      UserNotification.success(i18n.t("notifications.asset_updated", { ns: "enterprise_security_assets" }));
      invalidateAssetsTableQueries(queryClient);
      queryClient.invalidateQueries({
        queryKey: assetQueryKey(assetId)
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    updateAsset: mutateAsync,
    updatingAsset: isLoading
  };
}
export function useDeleteAsset() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteAsset,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.asset_deleted", { ns: "enterprise_security_assets" }));
      invalidateAssetsTableQueries(queryClient);
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    deleteAsset: mutateAsync,
    deletingAsset: isLoading
  };
}
export function useGetPriorities(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-asset-priorities"],
    queryFn: () => defaultOnError(
      fetchPriorities(),
      i18n.t("notifications.fetch_priorities_failed", { ns: "enterprise_security_assets" })
    ),
    retry: 2,
    enabled: execute
  });
  return {
    priorities: (data || []).slice().sort((a, b) => a.priority - b.priority),
    loadingPriorities: isLoading
  };
}
export function useGetPriorityMap(execute = true) {
  const { priorities, loadingPriorities } = useGetPriorities(execute);
  const serverText = useServerText();
  const priorityMap = useMemo(
    () => Object.fromEntries(priorities.map((priority) => [priority.priority, serverText(priority.text)])),
    [priorities, serverText]
  );
  return { priorityMap, loadingPriorities };
}
export function useGetCategories(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-all-categories"],
    queryFn: () => defaultOnError(
      fetchCategories(),
      i18n.t("notifications.fetch_categories_failed", { ns: "enterprise_security_assets" })
    ),
    retry: 2,
    enabled: execute
  });
  return {
    categories: data || [],
    loadingCategories: isLoading
  };
}
export function useAddPriority() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addPriority,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.priority_tag_added", { ns: "enterprise_security_assets" }));
      queryClient.invalidateQueries({
        queryKey: ["get-asset-priorities"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { addPriority: mutateAsync, addingPriority: isLoading };
}
export function useUpdatePriority() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updatePriority,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.priority_tag_updated", { ns: "enterprise_security_assets" }));
      queryClient.invalidateQueries({
        queryKey: ["get-asset-priorities"]
      });
      queryClient.invalidateQueries({
        queryKey: assetsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { updatePriority: mutateAsync, updatingPriority: isLoading };
}
export function useReprioritizePriorities() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: reprioritizePriorities,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.priority_tags_updated", { ns: "enterprise_security_assets" }));
      queryClient.invalidateQueries({
        queryKey: ["get-asset-priorities"]
      });
      queryClient.invalidateQueries({
        queryKey: assetsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { reprioritizePriorities: mutateAsync, reprioritizingPriorities: isLoading };
}
export function useDeletePriority() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deletePriority,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.priority_tag_deleted", { ns: "enterprise_security_assets" }));
      queryClient.invalidateQueries({
        queryKey: ["get-asset-priorities"]
      });
      queryClient.invalidateQueries({
        queryKey: assetsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { deletePriority: mutateAsync, deletingPriority: isLoading };
}
export function useAddCategory() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addCategory,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.category_tag_added", { ns: "enterprise_security_assets" }));
      queryClient.invalidateQueries({
        queryKey: ["get-all-categories"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { addCategory: mutateAsync, addingCategory: isLoading };
}
export function useUpdateCategory() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateCategory,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.category_tag_updated", { ns: "enterprise_security_assets" }));
      queryClient.invalidateQueries({
        queryKey: ["get-all-categories"]
      });
      queryClient.invalidateQueries({
        queryKey: assetsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { updateCategory: mutateAsync, updatingCategory: isLoading };
}
export function useDeleteCategory() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.category_tag_deleted", { ns: "enterprise_security_assets" }));
      queryClient.invalidateQueries({
        queryKey: ["get-all-categories"]
      });
      queryClient.invalidateQueries({
        queryKey: assetsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { deleteCategory: mutateAsync, deletingCategory: isLoading };
}
export function useBulkAddCategories() {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: bulkAddCategories,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.assets_updated", { ns: "enterprise_security_assets" }));
      invalidateAssetsTableQueries(queryClient);
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { bulkAddCategories: mutateAsync };
}
export function useGetAssetSources({
  page,
  perPage,
  query = void 0,
  orderBy = void 0,
  direction = void 0,
  filters = void 0
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-assets-sources", page, perPage, query, orderBy, direction, filters],
    queryFn: () => defaultOnError(
      fetchAssetSources(page, perPage, query, orderBy, direction, filters),
      i18n.t("notifications.fetch_asset_sources_failed", { ns: "enterprise_security_assets" })
    ),
    retry: 2,
    placeholderData: keepPreviousData
  });
  return {
    loadingAssetSources: isLoading,
    assetSources: isLoading ? [] : data == null ? void 0 : data.asset_source_backend_configs,
    pagination: {
      page: (data == null ? void 0 : data.page) || page,
      perPage: (data == null ? void 0 : data.per_page) || perPage,
      total: (data == null ? void 0 : data.total) || 0,
      grandTotal: (data == null ? void 0 : data.grand_total) || 0,
      count: (data == null ? void 0 : data.count) || 0
    }
  };
}
export function useGetAssetSource(sourceId) {
  const { data, isFetching } = useQuery({
    queryKey: ["get-asset-source", sourceId],
    queryFn: () => defaultOnError(
      fetchAssetSource(sourceId),
      i18n.t("notifications.fetch_asset_source_failed", { ns: "enterprise_security_assets" })
    ),
    retry: 2,
    enabled: !!sourceId
  });
  return {
    assetSource: data || null,
    loadingAssetSource: isFetching
  };
}
export function useGetMS365DeploymentTypes() {
  var _a;
  const { data, isFetching } = useQuery({
    queryKey: ["get-ms365-deployment-types"],
    queryFn: () => defaultOnError(
      fetchMS365DeploymentTypes(),
      i18n.t("notifications.fetch_ms365_deployment_types_failed", { ns: "enterprise_security_assets" })
    ),
    retry: 2
  });
  return {
    ms365DeploymentTypes: (data == null ? void 0 : data.types) || [],
    ms365DeploymentTypesMap: Object.fromEntries(
      ((_a = data == null ? void 0 : data.types) != null ? _a : []).map((dt) => [dt.value, dt.display_name])
    ),
    loadingMS365DeploymentTypes: isFetching
  };
}
export function useCreateAssetSource() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: createAssetSource,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.asset_source_created", { ns: "enterprise_security_assets" }));
      queryClient.invalidateQueries({
        queryKey: ["get-asset-source"]
      });
      queryClient.invalidateQueries({
        queryKey: ["get-assets-sources"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    createAssetSource: mutateAsync,
    creatingAssetSource: isLoading
  };
}
export function useUpdateAssetSource() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateAssetSource,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.asset_source_updated", { ns: "enterprise_security_assets" }));
      queryClient.invalidateQueries({
        queryKey: ["get-asset-source"]
      });
      queryClient.invalidateQueries({
        queryKey: ["get-assets-sources"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    updateAssetSource: mutateAsync,
    updatingAssetSource: isLoading
  };
}
export function useDeleteAssetSource() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteAssetSource,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.asset_source_deleted", { ns: "enterprise_security_assets" }));
      queryClient.invalidateQueries({
        queryKey: ["get-assets-sources"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    deleteAssetSource: mutateAsync,
    deletingAssetSource: isLoading
  };
}
export function useGetAssetSourceMappings({ page, perPage }, sourceId) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-asset-source-mappings", sourceId, page, perPage],
    queryFn: () => defaultOnError(
      fetchAssetSourceMappings(sourceId, page, perPage),
      i18n.t("notifications.fetch_asset_source_mappings_failed", { ns: "enterprise_security_assets" })
    ),
    retry: 2,
    enabled: !!sourceId,
    placeholderData: keepPreviousData
  });
  return {
    loadingAssetSourceMappings: isLoading,
    assetSourceMappings: isLoading ? [] : data == null ? void 0 : data.asset_import_backend_configs,
    pagination: {
      page: (data == null ? void 0 : data.page) || page,
      perPage: (data == null ? void 0 : data.per_page) || perPage,
      total: (data == null ? void 0 : data.total) || 0,
      grandTotal: (data == null ? void 0 : data.grand_total) || 0,
      count: (data == null ? void 0 : data.count) || 0
    }
  };
}
export function useGetAssetSourceMapping(sourceId, mappingId) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-asset-source-mapping", sourceId, mappingId],
    queryFn: () => defaultOnError(
      fetchAssetSourceMapping(sourceId, mappingId),
      i18n.t("notifications.fetch_asset_source_mapping_failed", { ns: "enterprise_security_assets" })
    ),
    retry: 2,
    enabled: !!mappingId
  });
  return {
    assetSourceMapping: data || null,
    loadingAssetSourceMapping: isLoading
  };
}
export function useCreateAssetSourceMapping() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: createAssetSourceMapping,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.asset_source_mapping_added", { ns: "enterprise_security_assets" })
      );
      queryClient.invalidateQueries({
        queryKey: ["get-asset-source-mappings"]
      });
    },
    onError: (error) => {
      if ("failed" in error) {
        UserNotification.error(
          Object.entries(error.errors).map(([key, errors]) => `${key}: ${errors.join("\n")}`).join("\n"),
          i18n.t("notifications.api_validation_failed", { ns: "enterprise_security_assets" })
        );
      } else UserNotification.error(error.message);
    }
  });
  return {
    createAssetSourceMapping: mutateAsync,
    creatingAssetSourceMapping: isLoading
  };
}
export function useUpdateAssetSourceMapping() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateAssetSourceMapping,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.asset_source_mapping_updated", { ns: "enterprise_security_assets" })
      );
      queryClient.invalidateQueries({
        queryKey: ["get-asset-source-mappings"]
      });
    },
    onError: (error) => {
      if ("failed" in error) {
        UserNotification.error(
          Object.entries(error.errors).map(([key, errors]) => `${key}: ${errors.join("\n")}`).join("\n"),
          i18n.t("notifications.api_validation_failed", { ns: "enterprise_security_assets" })
        );
      } else UserNotification.error(error.message);
    }
  });
  return {
    updateAssetSourceMapping: mutateAsync,
    updatingAssetSourceMapping: isLoading
  };
}
export function useDeleteAssetSourceMapping() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteAssetSourceMapping,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.asset_source_mapping_deleted", { ns: "enterprise_security_assets" })
      );
      queryClient.invalidateQueries({
        queryKey: ["get-asset-source-mappings"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    deleteAssetSourceMapping: mutateAsync,
    deletingAssetSourceMapping: isLoading
  };
}
export function useImportAssetsFromSource() {
  var _a;
  const [queryKey, setQueryKey] = useState();
  const queryClient = useQueryClient();
  return {
    importAssets: async (sourceId, mappingId) => new Promise((resolve) => {
      setQueryKey(["import-assets", sourceId, mappingId]);
      try {
        queryClient.fetchQuery({
          queryKey: ["import-assets", sourceId, mappingId],
          queryFn: () => importAssets(sourceId, mappingId)
        }).then((importData) => {
          if (importData.success === false) {
            UserNotification.error(
              importData.message,
              i18n.t("notifications.import_failed", { ns: "enterprise_security_assets" })
            );
          } else {
            UserNotification.success(
              i18n.t("notifications.import_successful", { ns: "enterprise_security_assets" })
            );
            queryClient.invalidateQueries({
              queryKey: ["get-assets-sources-mappings"]
            });
            queryClient.invalidateQueries({
              queryKey: ["get-asset-source"]
            });
          }
          resolve(importData);
        });
      } catch (error) {
        UserNotification.error(error.message);
        resolve(error);
      }
    }),
    importingAssets: ((_a = queryClient.getQueryState(queryKey)) == null ? void 0 : _a.fetchStatus) === "fetching"
  };
}
export function useTestAssetSource() {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: testAssetSource,
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    testAssetSource: mutateAsync,
    testingAssetSource: isLoading
  };
}
export function useTestAssetSourceMapping() {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: testNewAssetSourceMapping,
    onError: (error) => UserNotification.error(error.message),
    retry: 2
  });
  return {
    testAssetSourceNewMapping: mutateAsync,
    testingAssetSourceNewMapping: isLoading
  };
}
export function useBulkTestAssetSource() {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: bulkTestAssetSource,
    onError: (error) => UserNotification.error(error.message),
    retry: 2
  });
  return {
    testAssetSources: mutateAsync,
    testingAssetSources: isLoading
  };
}
export function useTestSavedAssetSourceMapping() {
  var _a;
  const [queryKey, setQueryKey] = useState();
  const queryClient = useQueryClient();
  return {
    testSavedAssetSourceMapping: async (sourceId, mappingId) => new Promise((resolve) => {
      setQueryKey(["test-saved-asset-source-mapping", sourceId, mappingId]);
      try {
        queryClient.fetchQuery({
          queryKey: ["test-saved-asset-source-mapping", sourceId, mappingId],
          queryFn: () => testSavedAssetSourceMapping(sourceId, mappingId)
        }).then((testResponse) => {
          resolve(testResponse);
        });
      } catch (error) {
        UserNotification.error(error.message);
      }
    }),
    testingSavedAssetSourceMapping: ((_a = queryClient.getQueryState(queryKey)) == null ? void 0 : _a.fetchStatus) === "fetching"
  };
}
export function useToggleMappingSync() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: toggleMappingSync,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.asset_source_mapping_sync_updated", { ns: "enterprise_security_assets" })
      );
      queryClient.invalidateQueries({
        queryKey: ["get-asset-source-mappings"]
      });
    },
    onError: (error) => UserNotification.error(error.message),
    retry: 2
  });
  return {
    toggleMappingSync: mutateAsync,
    togglingMappingSync: isLoading
  };
}
export function useGetScanners({
  page,
  perPage,
  query = void 0,
  orderBy = void 0,
  direction = void 0,
  filters = void 0
}) {
  var _a;
  const { data, isLoading } = useQuery({
    queryKey: ["get-scanners", page, perPage, query, orderBy, direction, filters],
    queryFn: () => defaultOnError(
      fetchScanners(page, perPage, query, orderBy, direction, filters),
      i18n.t("notifications.fetch_scanners_failed", { ns: "enterprise_security_assets" })
    ),
    retry: 2,
    placeholderData: keepPreviousData
  });
  return {
    loadingScanners: isLoading,
    scanners: isLoading ? [] : (_a = data == null ? void 0 : data.vulnerability_scanners) != null ? _a : [],
    pagination: {
      page: (data == null ? void 0 : data.page) || page,
      perPage: (data == null ? void 0 : data.per_page) || perPage,
      total: (data == null ? void 0 : data.total) || 0,
      grandTotal: (data == null ? void 0 : data.grand_total) || 0,
      count: (data == null ? void 0 : data.count) || 0
    }
  };
}
export function useAddScanner() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addScanner,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.scanner_added", { ns: "enterprise_security_assets" }));
      queryClient.invalidateQueries({
        queryKey: ["get-scanners"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    addScanner: mutateAsync,
    addingScanner: isLoading
  };
}
export function useDeleteScanner() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteScanner,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.scanner_deleted", { ns: "enterprise_security_assets" }));
      queryClient.invalidateQueries({
        queryKey: ["get-scanners"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    deleteScanner: mutateAsync,
    deletingScanner: isLoading
  };
}
export function useEditScanner() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: editScanner,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.scanner_updated", { ns: "enterprise_security_assets" }));
      queryClient.invalidateQueries({
        queryKey: ["get-scanners"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    editScanner: mutateAsync,
    editingScanner: isLoading
  };
}
export function useToggleScannerSync() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: toggleScannerSync,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.scanner_updated", { ns: "enterprise_security_assets" }));
      queryClient.invalidateQueries({
        queryKey: ["get-scanners"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    toggleScannerSync: mutateAsync,
    togglingScannerSync: isLoading
  };
}
export function useTestNewScannerConnection() {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: testNewScannerConnection,
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    testNewScannerConnection: mutateAsync,
    testingNewScannerConnection: isLoading
  };
}
export function useTestScannerConnection() {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: testScannerConnection,
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    testScannerConnection: mutateAsync,
    testingScannerConnection: isLoading
  };
}
export function useImportVulnerabilityScans() {
  var _a;
  const [queryKey, setQueryKey] = useState();
  const queryClient = useQueryClient();
  return {
    importVulnerabilityScans: async (scannerId) => new Promise((resolve) => {
      setQueryKey(["import-vulnerability-scans", scannerId]);
      try {
        queryClient.fetchQuery({
          queryKey: ["import-vulnerability-scans", scannerId],
          queryFn: () => importVulnerabilityScans(scannerId)
        }).then((importResult) => {
          queryClient.invalidateQueries({
            queryKey: ["get-scanners"]
          });
          if (importResult.success) UserNotification.success(importResult.message);
          if (!importResult.success) UserNotification.error(importResult.errors.join(", "), importResult.message);
          resolve(importResult);
        });
      } catch (error) {
        UserNotification.error(error.message);
      }
    }),
    importingVulnerabilityScans: ((_a = queryClient.getQueryState(queryKey)) == null ? void 0 : _a.fetchStatus) === "fetching"
  };
}
